import { ref } from 'vue'
import type { FieldConfig, FormData, Section } from '@/types/index'

export function useFormConfig () {
  const form = ref<FormData>({
    builder: 'no',
    uniquePages: 'no',
    designPreference: 'no',
    mobileApps: 'no',
    languages: 'onlyBahasa',
    features: {
      contentEditor: false,
      hosting: false,
      domain: false,
      webAd: false,
      seo: false,
      businessEmail: false,
      ecommerce: false,
    },
    logo: 'no',
    logoFeatures: {
      businessCard: false,
      stationeries: false,
      idCard: false,
      customMockups: false,
    },
    packaging: 'no',
    photography: 'no',
    catalog: 'no',
    marketplace: 'no',
    socialMedia: {
      type: 'no',
      count: 'oneToThree',
      frequency: 'monthly',
    },
    mascot2d: 'no',
    mascot3d: 'no',
    shortVideo: {
      type: 'no',
      count: 'oneToThree',
      frequency: 'monthly',
    },
    liveStreaming: {
      type: 'no',
      count: 'oneToThree',
      frequency: 'monthly',
    },
  })

  const sections: Section[] = [
    { id: 'website-apps', title: 'Website & Apps' },
    { id: 'design', title: 'Design' },
    { id: 'video', title: 'Video' },
  ]

  const sectionFields: Record<string, FieldConfig[]> = {
    'website-apps': [
      {
        type: 'radio',
        key: 'builder',
        label: 'Builder',
        description: 'WordPress offers a balance of flexibility and ease of use, while Custom development provides complete control over functionality and features.',
        options: [
          { value: 'no', label: 'No' },
          { value: 'wordpress', label: 'Wordpress' },
          { value: 'custom', label: 'Custom' },
        ],
      },
      {
        type: 'range',
        key: 'uniquePages',
        label: 'Unique Pages',
        description: 'Indicate the number of distinct pages your website needs.',
        rangeLabels: ['1', '2-9', '10-19', '20+'],
        rangeValues: ['one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
      {
        type: 'radio',
        key: 'designPreference',
        label: 'Design Preference',
        description: 'Select your desired level of design customization based on your brand requirements.',
        options: [
          { value: 'no', label: 'No Design' },
          { value: 'simple', label: 'Simple Design' },
          { value: 'custom', label: 'Custom' },
        ],
      },
      {
        type: 'range',
        key: 'languages',
        label: 'Languages',
        description: 'Reach a wider audience by choosing single or multi-language support for your website.',
        rangeLabels: ['Only Bahasa', 'Only English', 'Bahasa + English', 'Custom'],
        rangeValues: ['onlyBahasa', 'onlyEnglish', 'bahasaEnglish', 'custom'],
      },
      {
        type: 'radio',
        key: 'mobileApps',
        label: 'Mobile Apps',
        description: 'Specify your mobile application requirements based on target platforms.',
        options: [
          { value: 'no', label: 'No App' },
          { value: 'android', label: 'Android Only' },
          { value: 'ios', label: 'iOS Only' },
          { value: 'both', label: 'Android & iOS' },
        ],
      },
      {
        type: 'checkbox',
        key: 'features',
        label: 'Website Features',
        isDetail: true,
        description: 'Additional web functionality needed for your project.\n- Content Editor: Manage your own content\n- Hosting: Web hosting services\n- Domain: Domain name registration and management\n- Web Ad: Advertise the website or a single webpage\n- SEO: Search engine optimization features\n- Business Email: Professional email services\n- E-Commerce: Online shopping functionality',
        options: [
          { key: 'contentEditor', label: 'Content Editor' },
          { key: 'hosting', label: 'Hosting' },
          { key: 'domain', label: 'Domain' },
          { key: 'webAd', label: 'Web Ad' },
          { key: 'seo', label: 'SEO' },
          { key: 'businessEmail', label: 'Business Email' },
          { key: 'ecommerce', label: 'E-Commerce' },
        ],
      },
    ],
    design: [
      {
        type: 'radio',
        key: 'logo',
        label: 'Logo',
        description: 'Choose your logo design requirements, either basic logo design or full custom logo design with multiple concepts.',
        options: [
          { value: 'no', label: 'No' },
          { value: 'basic', label: 'Basic' },
          { value: 'custom', label: 'Custom' },
        ],
      },
      {
        type: 'checkbox',
        key: 'logoFeatures',
        label: 'Logo Features',
        isDetail: true,
        description: 'Additional branding elements needed for your project.\n- Logo Design: Custom logo creation for brand identity\n- Logo Variations: Multiple versions for different uses (e.g., color, monochrome)\n- Logo Guidelines: Usage rules and style guide\n- Logo Files: High-resolution formats (e.g., PNG, SVG, EPS)',
        options: [
          { key: 'businessCard', label: 'Business Card' },
          { key: 'stationeries', label: 'Stationeries' },
          { key: 'idCard', label: 'ID Card' },
          { key: 'customMockups', label: 'Custom Mockups' },
        ],
      },
      {
        type: 'radio',
        key: 'packaging',
        label: 'Packaging',
        sublabel: '(per item)',
        description: 'Select the complexity level of your packaging design needs.',
        options: [
          { value: 'no', label: 'No' },
          { value: 'simple', label: 'Simple' },
          { value: 'advanced', label: 'Advanced' },
        ],
      },
      {
        type: 'range',
        key: 'photography',
        label: 'Product Photography',
        sublabel: '(shots)',
        rangeLabels: ['No', '1', '2-9', '10-19', '20+'],
        rangeValues: ['no', 'one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
      {
        type: 'range',
        key: 'catalog',
        label: 'Brochure/Catalog',
        sublabel: '(pages)',
        rangeLabels: ['No', '1', '2-9', '10-19', '20+'],
        rangeValues: ['no', 'one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
      {
        type: 'range',
        key: 'marketplace',
        label: 'Marketplace',
        sublabel: '(products)',
        rangeLabels: ['No', '1', '2-9', '10-19', '20+'],
        rangeValues: ['no', 'one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
      {
        type: 'radio',
        key: 'socialMedia.type',
        label: 'Social Media',
        description: 'Choose your social media content creation frequency.',
        options: [
          { value: 'no', label: 'No' },
          { value: 'fixed', label: 'Fixed Amount' },
          { value: 'regularly', label: 'Regularly' },
        ],
      },
      {
        type: 'range',
        key: 'socialMedia.count',
        rangeLabels: ['1-3', '4-6', '7-9', '10+'],
        rangeValues: ['oneToThree', 'fourToSix', 'sevenToNine', 'tenPlus'],
        condition: {
          key: 'socialMedia.type',
          values: ['fixed', 'regularly'],
        },
      },
      {
        type: 'select',
        key: 'socialMedia.frequency',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'weekly', label: 'Weekly' },
        ],
        condition: {
          key: 'socialMedia.type',
          values: ['regularly'],
        },
      },
      {
        type: 'range',
        key: 'mascot2d',
        label: '2D Mascot',
        sublabel: '(characters)',
        rangeLabels: ['No', '1', '2-9', '10-19', '20+'],
        rangeValues: ['no', 'one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
      {
        type: 'range',
        key: 'mascot3d',
        label: '3D Mascot',
        sublabel: '(characters)',
        rangeLabels: ['No', '1', '2-9', '10-19', '20+'],
        rangeValues: ['no', 'one', 'twoToNine', 'tenToNineteen', 'twentyPlus'],
      },
    ],
    video: [
      {
        type: 'radio',
        key: 'shortVideo.type',
        label: 'Short Video',
        sublabel: '(less than 3 minutes)',
        options: [
          { value: 'no', label: 'No' },
          { value: 'fixed', label: 'Fixed Amount (Once)' },
          { value: 'regularly', label: 'Regularly' },
        ],
      },
      {
        type: 'range',
        key: 'shortVideo.count',
        rangeLabels: ['1-3', '4-6', '7-9', '10+'],
        rangeValues: ['oneToThree', 'fourToSix', 'sevenToNine', 'tenPlus'],
        condition: {
          key: 'shortVideo.type',
          values: ['fixed', 'regularly'],
        },
      },
      {
        type: 'select',
        key: 'shortVideo.frequency',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'weekly', label: 'Weekly' },
        ],
        condition: {
          key: 'shortVideo.type',
          values: ['regularly'],
        },
      },
      {
        type: 'radio',
        key: 'liveStreaming.type',
        label: 'Live Streaming',
        options: [
          { value: 'no', label: 'No' },
          { value: 'fixed', label: 'Fixed Amount (Once)' },
          { value: 'regularly', label: 'Regularly' },
        ],
      },
      {
        type: 'range',
        key: 'liveStreaming.count',
        rangeLabels: ['1-3', '4-6', '7-9', '10+'],
        rangeValues: ['oneToThree', 'fourToSix', 'sevenToNine', 'tenPlus'],
        condition: {
          key: 'liveStreaming.type',
          values: ['fixed', 'regularly'],
        },
      },
      {
        type: 'select',
        key: 'liveStreaming.frequency',
        options: [
          { value: 'monthly', label: 'Monthly' },
          { value: 'weekly', label: 'Weekly' },
        ],
        condition: {
          key: 'liveStreaming.type',
          values: ['regularly'],
        },
      },
    ],
  }

  return {
    form,
    sections,
    sectionFields,
  }
}
